import { useEffect } from 'react';

/**
 * Système de reveal au scroll pour tous les éléments [data-reveal].
 *
 * - Ajoute `reveal-ready` sur <html> : le CSS ne masque les éléments que si
 *   le JS est actif (fallback : tout reste visible sans JS / sans IO).
 * - IntersectionObserver unique + MutationObserver pour les éléments montés
 *   après coup (changement de langue, pagination, etc.).
 * - Respecte prefers-reduced-motion : aucun masquage, aucun observer.
 */
export default function useScrollReveal() {
  useEffect(() => {
    const root = document.documentElement;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (reduced.matches || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    root.classList.add('reveal-ready');

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );

    const observeWithin = (node) => {
      if (node.nodeType !== 1) return;
      if (node.matches?.('[data-reveal]') && !node.classList.contains('is-revealed')) {
        io.observe(node);
      }
      node
        .querySelectorAll?.('[data-reveal]:not(.is-revealed)')
        .forEach((el) => io.observe(el));
    };

    observeWithin(document.body);

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(observeWithin);
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    const onReducedChange = (event) => {
      if (event.matches) {
        root.classList.remove('reveal-ready');
        mo.disconnect();
        io.disconnect();
      }
    };
    reduced.addEventListener('change', onReducedChange);

    return () => {
      reduced.removeEventListener('change', onReducedChange);
      mo.disconnect();
      io.disconnect();
      root.classList.remove('reveal-ready');
    };
  }, []);
}
